/**
 * EngagementProgressingGrid
 *
 * Wide, grouped-header table matching the uploaded screenshot/spreadsheet:
 * a 2-row header (group band + sub-column labels), mouse hold-and-drag
 * horizontal scroll (same pattern as ComparisonGrid), a checkbox in each
 * of the three "Total Hours" cells (Pending TRS / Approved TRS / Booked
 * SSS) that toggles that row+category's drill-down detail panel, and
 * "No" + "Client name" pinned as sticky left columns so they stay visible
 * while scrolling through the other 30 columns.
 */
import { useRef, useState } from 'react';
import { PROGRESSING_COLUMNS } from '../../data/sampleEngagementProgressingData.js';
import { formatDateRange } from '../../utils/formatDate.js';

const currency = (n) =>
  typeof n === 'number' ? n.toLocaleString(undefined, { style: 'currency', currency: 'USD' }) : n;

/** Columns pinned to the left while scrolling, with their fixed widths/offsets. */
const STICKY_COLUMNS = {
  no: { widthClass: 'w-14 min-w-[56px] max-w-[56px]', leftClass: 'left-0' },
  clientName: { widthClass: 'w-56 min-w-[224px] max-w-[224px]', leftClass: 'left-14' },
};

/**
 * Builds the header's group band: one cell per contiguous run of columns
 * sharing the same `group` AND the same sticky-ness -- a sticky column
 * always gets its own band cell (even mid-group), since a single wide
 * <th colSpan> can't be partially sticky.
 */
function buildGroupBand(columns) {
  const band = [];
  let i = 0;
  while (i < columns.length) {
    const { group } = columns[i];
    const sticky = Boolean(STICKY_COLUMNS[columns[i].key]);
    let span = 1;
    while (
      i + span < columns.length &&
      columns[i + span].group === group &&
      Boolean(STICKY_COLUMNS[columns[i + span].key]) === sticky
    ) {
      span += 1;
    }
    band.push({ group, span, sticky, startKey: columns[i].key, key: `${group ?? 'ungrouped'}-${i}` });
    i += span;
  }
  return band;
}

const GROUP_BAND = buildGroupBand(PROGRESSING_COLUMNS);

/** @param {string|undefined} key */
function stickyCellClasses(key) {
  const sticky = STICKY_COLUMNS[key];
  if (!sticky) return '';
  return `sticky ${sticky.leftClass} ${sticky.widthClass} z-10 bg-surface group-hover:bg-surface-hover`;
}

/**
 * @param {{
 *   rows: Record<string, any>[],
 *   selectedKeys: Set<string>,
 *   onToggle: (rowId: string, category: 'pending'|'approved'|'booked') => void,
 * }} props
 */
export const EngagementProgressingGrid = ({ rows, selectedKeys, onToggle }) => {
  const scrollRef = useRef(/** @type {HTMLDivElement|null} */ (null));
  const dragState = useRef({ isPointerDown: false, isDragging: false, startX: 0, startScrollLeft: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const handleMouseDown = (/** @type {import('react').MouseEvent} */ e) => {
    if (!scrollRef.current) return;
    // Don't hijack clicks on the checkboxes/labels -- only the empty
    // table area (and plain cells) should start a drag-scroll.
    if (/** @type {HTMLElement} */ (e.target).closest('input, label, button')) return;
    dragState.current = {
      isPointerDown: true,
      isDragging: false,
      startX: e.pageX,
      startScrollLeft: scrollRef.current.scrollLeft,
    };
  };

  const handleMouseMove = (/** @type {import('react').MouseEvent} */ e) => {
    if (!dragState.current.isPointerDown || !scrollRef.current) return;
    const delta = e.pageX - dragState.current.startX;
    // Small threshold before treating this as a drag, so a slight wobble
    // during a normal click doesn't scroll the table out from under it.
    if (!dragState.current.isDragging && Math.abs(delta) < 4) return;
    dragState.current.isDragging = true;
    setIsDragging(true);
    scrollRef.current.scrollLeft = dragState.current.startScrollLeft - delta;
  };

  const stopDragging = () => {
    dragState.current.isPointerDown = false;
    dragState.current.isDragging = false;
    setIsDragging(false);
  };

  return (
    <div className="rounded-lg border border-edge bg-surface">
      <div
        ref={scrollRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={stopDragging}
        onMouseLeave={stopDragging}
        className={`overflow-x-auto ${isDragging ? 'cursor-grabbing select-none' : 'cursor-grab'}`}
      >
        <table className="w-full min-w-[2400px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-edge/60 text-left text-[11px] uppercase tracking-wide text-muted">
              {GROUP_BAND.map((g) => (
                <th
                  key={g.key}
                  colSpan={g.span}
                  className={`whitespace-nowrap border-b border-edge bg-surface px-3 py-2 text-center font-semibold ${
                    g.sticky ? `sticky z-20 ${STICKY_COLUMNS[g.startKey].leftClass}` : ''
                  }`}
                >
                  {g.sticky ? '' : (g.group ?? '')}
                </th>
              ))}
            </tr>
            <tr className="border-b border-edge text-left text-xs uppercase tracking-wide text-muted">
              {PROGRESSING_COLUMNS.map((col) => (
                <th
                  key={col.key}
                  className={`whitespace-nowrap bg-surface px-3 py-2 font-medium ${
                    STICKY_COLUMNS[col.key] ? `sticky z-20 ${STICKY_COLUMNS[col.key].leftClass} border-r border-edge` : ''
                  }`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="group border-b border-edge/60 text-primary hover:bg-surface-hover">
                {PROGRESSING_COLUMNS.map((col) => {
                  const value = row[col.key];
                  // Substring match on 'budget'/'charges' would also catch
                  // budgetHours (an hour count, not a dollar amount) --
                  // explicitly excluding any 'hours' key guards against
                  // this same mistake recurring as more columns get wired.
                  const key = col.key.toLowerCase();
                  const isCurrency = !key.includes('hours') && (key.includes('charges') || key.includes('budget') || col.key === 'elApprovedFee');
                  const display = col.key === 'period'
                    ? formatDateRange(value)
                    : col.format === 'percent' && typeof value === 'number'
                      ? `${(value * 100).toFixed(2)}%`
                      : isCurrency && typeof value === 'number'
                        ? currency(value)
                        : value;
                  const sticky = STICKY_COLUMNS[col.key];

                  return (
                    <td
                      key={col.key}
                      title={sticky && typeof display === 'string' ? display : undefined}
                      className={`px-3 py-2 ${sticky ? 'overflow-hidden text-ellipsis whitespace-nowrap' : 'whitespace-nowrap'} ${stickyCellClasses(col.key)} ${
                        sticky ? 'border-r border-edge' : ''
                      }`}
                    >
                      {col.checkable ? (
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={selectedKeys.has(`${row.id}:${col.checkable}`)}
                            onChange={() => onToggle(row.id, col.checkable)}
                            className="h-3.5 w-3.5 rounded border-edge bg-surface-hover text-accent"
                          />
                          <span className={value > 0 ? 'font-medium text-primary' : 'text-muted'}>{display}</span>
                        </label>
                      ) : (
                        display
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
